import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ErrorToast from '../../components/ui/ErrorToast';
import ChangePasswordModal from '../../components/ui/ChangePasswordModal';
import { fetchMyFranchiseeRestaurants, bulkUpdateRestaurantProfiles, bulkAddDishToRestaurants } from '../../services/franchiseeService';



let _toastId = 0;
const newToastId = () => `toast-${++_toastId}`;
const DOMAIN = 'scann3r.ca';

/* ─── Bulk Profile Edit Modal ──────────────────────────────────────── */
const BulkProfileModal = ({ restaurants, onClose, addToast }) => {
  const [form, setForm] = useState({ phone: '', address: '', hours: '' });
  const [selectedIds, setSelectedIds] = useState(restaurants?.map((r) => r?.id) || []);
  const [loading, setLoading] = useState(false);

  const toggleRestaurant = (id) => {
    setSelectedIds((prev) =>
      prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const updates = {};
    if (form?.phone?.trim()) updates.phone = form?.phone?.trim();
    if (form?.address?.trim()) updates.address = form?.address?.trim();
    if (form?.hours?.trim()) updates.hours = form?.hours?.trim();
    if (!Object.keys(updates)?.length) {
      addToast('Please fill in at least one field to update.', 'error');
      return;
    }
    if (!selectedIds?.length) {
      addToast('Please select at least one restaurant.', 'error');
      return;
    }
    setLoading(true);
    try {
      await bulkUpdateRestaurantProfiles(selectedIds, updates);
      addToast(`Updated ${selectedIds?.length} restaurant(s) successfully.`, 'success');
      onClose();
    } catch (err) {
      addToast(err?.message || 'Bulk update failed.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Layers" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Bulk Profile Update</h2>
              <p className="text-xs text-muted-foreground">Apply changes to multiple restaurants at once</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Restaurant selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Apply to restaurants ({selectedIds?.length}/{restaurants?.length} selected)
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto border border-border rounded-xl p-3 bg-muted/20">
              {restaurants?.map((r) => (
                <label key={r?.id} className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedIds?.includes(r?.id)}
                    onChange={() => toggleRestaurant(r?.id)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30"
                  />
                  <span className="text-sm text-foreground group-hover:text-primary transition-colors">{r?.display_name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{r?.slug}.{DOMAIN}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={() => setSelectedIds(restaurants?.map((r) => r?.id))} className="text-xs text-primary hover:underline">Select all</button>
              <span className="text-xs text-muted-foreground">·</span>
              <button type="button" onClick={() => setSelectedIds([])} className="text-xs text-muted-foreground hover:text-foreground">Deselect all</button>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <p className="text-xs text-muted-foreground mb-4">Leave fields blank to keep existing values unchanged.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Phone Number</label>
                <Input value={form?.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e?.target?.value }))} placeholder="+1 555 123 4567" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Address</label>
                <Input value={form?.address} onChange={(e) => setForm((p) => ({ ...p, address: e?.target?.value }))} placeholder="123 Main St, City" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Opening Hours</label>
                <Input value={form?.hours} onChange={(e) => setForm((p) => ({ ...p, hours: e?.target?.value }))} placeholder="Mon-Sun: 11:00 AM - 10:00 PM" />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <Button type="submit" disabled={loading || !selectedIds?.length} className="flex-1 flex items-center justify-center gap-2">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Updating...</>
              ) : (
                <><Icon name="Save" size={15} />Apply to {selectedIds?.length} Restaurant{selectedIds?.length !== 1 ? 's' : ''}</>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-shrink-0">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Bulk Add Dish Modal ──────────────────────────────────────────── */
const BulkAddDishModal = ({ restaurants, onClose, addToast }) => {
  const [form, setForm] = useState({ name: '', description: '', price: '', category: 'seasonal', is_new: true });
  const [selectedIds, setSelectedIds] = useState(restaurants?.map((r) => r?.id) || []);
  const [loading, setLoading] = useState(false);

  const toggleRestaurant = (id) =>
    setSelectedIds((prev) => prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.name?.trim() || !form?.price) {
      addToast('Dish name and price are required.', 'error');
      return;
    }
    if (!selectedIds?.length) {
      addToast('Please select at least one restaurant.', 'error');
      return;
    }
    setLoading(true);
    try {
      const dishData = {
        name: form?.name?.trim(),
        description: form?.description?.trim() || '',
        price: parseFloat(form?.price) || 0,
        category: form?.category?.trim() || 'seasonal',
        is_new: form?.is_new,
        hidden: false,
        sort_order: 0,
        scan_count: 0,
      };
      await bulkAddDishToRestaurants(selectedIds, dishData);
      addToast(`Dish added to ${selectedIds?.length} restaurant(s) successfully.`, 'success');
      onClose();
    } catch (err) {
      addToast(err?.message || 'Failed to add dish.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-green-500/10 rounded-xl flex items-center justify-center">
              <Icon name="UtensilsCrossed" size={18} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Add Dish to All Restaurants</h2>
              <p className="text-xs text-muted-foreground">Add a seasonal or new dish across your restaurants</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Restaurant selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Add to restaurants ({selectedIds?.length}/{restaurants?.length} selected)
            </label>
            <div className="space-y-2 max-h-36 overflow-y-auto border border-border rounded-xl p-3 bg-muted/20">
              {restaurants?.map((r) => (
                <label key={r?.id} className="flex items-center gap-2.5 cursor-pointer group">
                  <input type="checkbox" checked={selectedIds?.includes(r?.id)} onChange={() => toggleRestaurant(r?.id)} className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30" />
                  <span className="text-sm text-foreground group-hover:text-primary transition-colors">{r?.display_name}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={() => setSelectedIds(restaurants?.map((r) => r?.id))} className="text-xs text-primary hover:underline">Select all</button>
              <span className="text-xs text-muted-foreground">·</span>
              <button type="button" onClick={() => setSelectedIds([])} className="text-xs text-muted-foreground hover:text-foreground">Deselect all</button>
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Dish Name <span className="text-destructive">*</span></label>
              <Input value={form?.name} onChange={(e) => setForm((p) => ({ ...p, name: e?.target?.value }))} placeholder="e.g. Seasonal Pumpkin Soup" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
              <Input value={form?.description} onChange={(e) => setForm((p) => ({ ...p, description: e?.target?.value }))} placeholder="Short description..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Price ($) <span className="text-destructive">*</span></label>
                <Input type="number" min="0" step="0.01" value={form?.price} onChange={(e) => setForm((p) => ({ ...p, price: e?.target?.value }))} placeholder="12.99" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Category</label>
                <Input value={form?.category} onChange={(e) => setForm((p) => ({ ...p, category: e?.target?.value }))} placeholder="seasonal" />
              </div>
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form?.is_new} onChange={(e) => setForm((p) => ({ ...p, is_new: e?.target?.checked }))} className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30" />
              <span className="text-sm text-foreground">Mark as "New" item</span>
            </label>
          </div>

          <div className="flex gap-3 pt-1">
            <Button type="submit" disabled={loading || !selectedIds?.length} className="flex-1 flex items-center justify-center gap-2">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Adding...</>
              ) : (
                <><Icon name="Plus" size={15} />Add to {selectedIds?.length} Restaurant{selectedIds?.length !== 1 ? 's' : ''}</>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-shrink-0">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Change Credentials Modal ────────────────────────────────────── */
const ChangeCredentialsModal = ({ restaurant, onClose, addToast }) => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.email?.trim() && !form?.password?.trim()) {
      setError('Please enter a new email or password.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke(
        'update-restaurant-user-credentials',
        {
          body: {
            restaurant_id: restaurant?.id,
            ...(form?.email?.trim() ? { new_email: form?.email?.trim() } : {}),
            ...(form?.password?.trim() ? { new_password: form?.password?.trim() } : {}),
          },
        },
      );
      if (fnError || data?.error) {
        throw new Error(data?.error || fnError?.message || 'Update failed');
      }
      addToast(`Credentials updated for "${restaurant?.display_name}"`, 'success');
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to update credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="KeyRound" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Change Credentials</h2>
              <p className="text-xs text-muted-foreground truncate max-w-[180px]">{restaurant?.display_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-xs text-muted-foreground">
            Leave a field blank to keep it unchanged. Only the restaurant admin account will be updated.
          </p>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">New Email</label>
            <Input type="email" value={form?.email} onChange={(e) => setForm((p) => ({ ...p, email: e?.target?.value }))} placeholder="new@email.com" autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">New Password</label>
            <Input type="password" value={form?.password} onChange={(e) => setForm((p) => ({ ...p, password: e?.target?.value }))} placeholder="••••••••" />
          </div>
          {error && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <Button type="submit" disabled={loading} className="flex-1 flex items-center justify-center gap-2">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Updating...</>
              ) : (
                <><Icon name="Save" size={15} />Update Credentials</>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-shrink-0">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Restaurant Row ───────────────────────────────────────────────── */
const FranchiseeRestaurantRow = ({ restaurant, onToggleStatus, addToast }) => {
  const navigate = useNavigate();
  const [showCredentials, setShowCredentials] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = `https://${restaurant?.slug}.${DOMAIN}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      addToast('Could not copy to clipboard');
    }
  };

  return (
    <div className="flex items-center gap-4 px-6 py-4 hover:bg-muted/20 transition-colors group">
      {/* Icon + name */}
      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon name="Store" size={18} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{restaurant?.display_name}</p>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground hover:text-primary transition-colors group/url"
          title="Copy URL"
        >
          <Icon name="Globe" size={11} className="flex-shrink-0" />
          <span className="truncate">{url}</span>
          <Icon
            name={copied ? 'Check' : 'Copy'}
            size={11}
            className={`flex-shrink-0 ml-0.5 opacity-0 group-hover/url:opacity-100 transition-opacity ${copied ? 'text-green-500' : ''}`}
          />
        </button>
      </div>
      {/* Status badge */}
      <span className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${restaurant?.status === 'inactive' ? 'bg-amber-500/10 text-amber-600' : 'bg-green-500/10 text-green-600'}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${restaurant?.status === 'inactive' ? 'bg-amber-500' : 'bg-green-500'}`} />
        {restaurant?.status === 'inactive' ? 'Inactive' : 'Active'}
      </span>
      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="text-xs flex items-center gap-1.5 h-8"
          onClick={() => window.open(url, '_blank')}
          title="Open menu"
        >
          <Icon name="ExternalLink" size={12} />
          <span className="hidden sm:inline">View Menu</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs flex items-center gap-1.5 h-8"
          onClick={() => navigate(`/restaurant-admin-dashboard?restaurant_id=${restaurant?.id}`)}
          title="Manage restaurant"
        >
          <Icon name="Settings" size={12} />
          <span className="hidden sm:inline">Manage</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs flex items-center gap-1.5 h-8"
          onClick={() => setShowCredentials(true)}
          title="Change admin credentials"
        >
          <Icon name="KeyRound" size={12} />
          <span className="hidden sm:inline">Credentials</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-8"
          onClick={() => onToggleStatus(restaurant)}
          title="Toggle status"
        >
          {restaurant?.status === 'inactive' ? 'Activate' : 'Deactivate'}
        </Button>
      </div>
      {showCredentials && (
        <ChangeCredentialsModal
          restaurant={restaurant}
          onClose={() => setShowCredentials(false)}
          addToast={addToast}
        />
      )}
    </div>
  );
};

/* ─── Main Franchisee Dashboard ────────────────────────────────────── */
const FranchiseeDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, isFranchisee, signIn, signOut, loading, profileLoading } = useAuth();

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [restaurants, setRestaurants] = useState([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showBulkProfile, setShowBulkProfile] = useState(false);
  const [showBulkDish, setShowBulkDish] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const [errorToasts, setErrorToasts] = useState([]);
  const addToast = useCallback((message, type = 'error') =>
    setErrorToasts((prev) => [...prev, { id: newToastId(), message, type }]), []);
  const dismissToast = (id) => setErrorToasts((prev) => prev?.filter((t) => t?.id !== id));

  const loadRestaurants = useCallback(async () => {
    setRestaurantsLoading(true);
    try {
      const data = await fetchMyFranchiseeRestaurants();
      setRestaurants(data);
    } catch (err) {
      addToast('Failed to load restaurants: ' + (err?.message || 'Unknown error'));
    } finally {
      setRestaurantsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    if (user && isFranchisee) {
      loadRestaurants();
    }
  }, [user, isFranchisee, loadRestaurants]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const { error } = await signIn(loginForm?.email, loginForm?.password);
      if (error) setLoginError(error?.message || 'Invalid credentials');
    } catch {
      setLoginError('Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleToggleStatus = async (restaurant) => {
    const newStatus = restaurant?.status === 'inactive' ? 'active' : 'inactive';
    try {
      const { error } = await supabase
        ?.from('restaurants')
        ?.update({ status: newStatus })
        ?.eq('id', restaurant?.id);
      if (error) throw error;
      setRestaurants((prev) =>
        prev?.map((r) => (r?.id === restaurant?.id ? { ...r, status: newStatus } : r))
      );
      addToast(
        `"${restaurant?.display_name}" ${newStatus === 'active' ? 'activated' : 'deactivated'}.`,
        'success'
      );
    } catch (err) {
      addToast('Failed to update status: ' + (err?.message || 'Unknown error'));
    }
  };

  const activeCount = restaurants?.filter((r) => r?.status !== 'inactive')?.length;

  const filteredRestaurants = restaurants?.filter((r) =>
    r?.display_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
    r?.slug?.toLowerCase()?.includes(search?.toLowerCase())
  );

  /* ── Loading ── */
  if (loading || (profileLoading && !userProfile)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  /* ── Login ── */
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Building2" size={26} className="text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">Franchisé</h1>
              <p className="text-sm text-muted-foreground mt-1">Portail de gestion des restaurants</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Courriel</label>
                <Input type="email" value={loginForm?.email} onChange={(e) => setLoginForm((p) => ({ ...p, email: e?.target?.value }))} placeholder="franchisee@scann3r.ca" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Mot de passe</label>
                <Input type="password" value={loginForm?.password} onChange={(e) => setLoginForm((p) => ({ ...p, password: e?.target?.value }))} placeholder="••••••••" required />
              </div>
              {loginError && <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{loginError}</p>}
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {loginLoading ? (
                  <span className="flex items-center gap-2 justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Connexion...
                  </span>
                ) : 'Se connecter'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button type="button" onClick={() => setShowChangePassword(true)} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 mx-auto">
                <Icon name="KeyRound" size={14} />
                Changer le mot de passe
              </button>
            </div>
          </div>
        </div>
        {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} resetMode={true} />}
      </div>
    );
  }

  /* ── Wrong role ── */
  if (!isFranchisee) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-14 h-14 bg-destructive/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Icon name="ShieldX" size={26} className="text-destructive" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mb-4">Ce portail est réservé aux franchisés.</p>
          <Button variant="outline" onClick={() => navigate('/restaurant-admin-dashboard')}>Retour</Button>
        </div>
      </div>
    );
  }

  /* ── Franchisee Dashboard ── */
  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Building2" size={18} className="text-primary" />
            </div>
            <div>
              <h1 className="text-base font-bold text-foreground leading-none">Portail Franchisé</h1>
              <p className="text-xs text-muted-foreground">scann3r.ca</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:block truncate max-w-[180px]">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={() => setShowChangePassword(true)} className="flex items-center gap-1.5 h-8 hidden sm:flex">
              <Icon name="KeyRound" size={13} />
              Password
            </Button>
            <Button variant="outline" size="sm" onClick={signOut} className="flex items-center gap-1.5 h-8">
              <Icon name="LogOut" size={13} />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {/* Page title */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground">Tableau de bord</h2>
          <p className="text-sm text-muted-foreground mt-1">Gérez vos restaurants assignés</p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Store" size={16} className="text-primary" />
              </div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Restaurants</span>
            </div>
            <p className="text-3xl font-bold text-foreground">{restaurants?.length}</p>
            <p className="text-xs text-muted-foreground mt-1">Assignés à votre compte</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                <Icon name="CheckCircle" size={16} className="text-green-500" />
              </div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actifs</span>
            </div>
            <p className="text-3xl font-bold text-foreground">{activeCount}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {restaurants?.length - activeCount} inactif{restaurants?.length - activeCount !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5 col-span-2 sm:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={16} className="text-blue-500" />
              </div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions groupées</span>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs flex items-center gap-1.5 h-7 w-full justify-center"
                onClick={() => setShowBulkProfile(true)}
                disabled={!restaurants?.length}
              >
                <Icon name="Layers" size={12} />
                Modifier profils
              </Button>
              <Button
                size="sm"
                className="text-xs flex items-center gap-1.5 h-7 w-full justify-center"
                onClick={() => setShowBulkDish(true)}
                disabled={!restaurants?.length}
              >
                <Icon name="UtensilsCrossed" size={12} />
                Ajouter un plat
              </Button>
            </div>
          </div>
        </div>

        {/* Restaurant list */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-border">
            <div>
              <h2 className="text-base font-semibold text-foreground">Mes restaurants</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {restaurants?.length} restaurant{restaurants?.length !== 1 ? 's' : ''} assigné{restaurants?.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="relative">
              <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                placeholder="Rechercher..."
                className="pl-8 pr-3 h-9 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors w-48 text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {restaurantsLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Chargement des restaurants...</p>
              </div>
            </div>
          ) : filteredRestaurants?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <div className="w-14 h-14 bg-muted rounded-2xl flex items-center justify-center mb-4">
                <Icon name="Store" size={24} className="text-muted-foreground" />
              </div>
              {search ? (
                <>
                  <p className="text-sm font-medium text-foreground mb-1">Aucun résultat pour "{search}"</p>
                  <p className="text-xs text-muted-foreground">Essayez un autre nom.</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-foreground mb-1">Aucun restaurant assigné</p>
                  <p className="text-xs text-muted-foreground">Le Super Admin doit vous assigner des restaurants.</p>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredRestaurants?.map((restaurant) => (
                <FranchiseeRestaurantRow
                  key={restaurant?.id}
                  restaurant={restaurant}
                  onToggleStatus={handleToggleStatus}
                  addToast={addToast}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {showBulkProfile && (
        <BulkProfileModal
          restaurants={restaurants}
          onClose={() => setShowBulkProfile(false)}
          addToast={addToast}
        />
      )}
      {showBulkDish && (
        <BulkAddDishModal
          restaurants={restaurants}
          onClose={() => setShowBulkDish(false)}
          addToast={addToast}
        />
      )}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} resetMode={true} />
      )}

      <ErrorToast toasts={errorToasts} onDismiss={dismissToast} autoDismissMs={8000} />
    </div>
  );
};

export default FranchiseeDashboard;
